import { useMemo } from 'react';

import type {
	CommentLocation,
	CommentReply,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import {
	useCommentsDataState,
	type CommentData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expValEquals } from '@confluence/feature-experiments';

const createInlineMarkerMatcher =
	(parentLocation: CommentLocation) =>
	(reply: CommentReply | null): boolean => {
		if (!reply) {
			return false;
		}

		const replyLocation = reply.location as CommentLocation;

		return parentLocation.inlineMarkerRef === replyLocation.inlineMarkerRef;
	};

export const useIsEditorExpanded = (
	parentComment: CommentData,
	location: LocationValue,
): boolean => {
	const { hasNestedComments: legacyHasNestedComments } = useCommentsDataState();
	const processedCommentsData = useProcessedCommentsDataState();

	const hasNestedComments = expValEquals('cc_comments_new_comments_data_manager', 'isEnabled', true)
		? processedCommentsData.hasNestedComments
		: legacyHasNestedComments;

	const { replyToCommentId, location: commentLocation } = useCommentsPanelState();

	return useMemo(() => {
		if (location === LocationValue.FOOTER) {
			if (commentLocation === LocationValue.FOOTER) {
				return (
					parentComment.id === replyToCommentId ||
					// Editor management depends on reply nesting configuration:
					// - With nested replies: ObjectCommentNestableReplies handles the Editor
					// - Without nested replies: Open Editor when replyToComment query parameter
					//   matches a reply's ID
					(!hasNestedComments && parentComment.replies.some(({ id }) => id === replyToCommentId))
				);
			}

			return false;
		}

		const inlineMarkerMatcher = createInlineMarkerMatcher(
			parentComment.location as CommentLocation,
		);

		const parentReplies = parentComment.replies || [];
		const matchedReplies: CommentReply[] = parentReplies.filter(inlineMarkerMatcher);

		if (commentLocation === LocationValue.PANEL) {
			const isReplyingToParent = replyToCommentId === parentComment.id;
			const isReplyingToChild = matchedReplies.some(
				(reply: CommentReply) => reply.id === replyToCommentId,
			);

			const canExpandForChildReply =
				isReplyingToChild &&
				expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true);

			return isReplyingToParent || canExpandForChildReply;
		}

		return false;
	}, [
		commentLocation,
		hasNestedComments,
		location,
		parentComment.id,
		parentComment.location,
		parentComment.replies,
		replyToCommentId,
	]);
};
